import { useMemo } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  NavItem,
  Text,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import OpsLogo from '~/assets/svg/OpsLogo';
import AccountMenu from '~/components/common/navbar/AccountMenu';
import {
  NAV_BOTTOM_PADDING,
  NAV_HORIZONTAL_PADDING,
  NAV_TOP_PADDING,
} from '~/components/common/navbar/constants';
import { SIDE_NAV_WIDTH } from '~/lib/constants';
import { VITE_MAUD_APP_VERSION } from '~/lib/constants/env';
import { OpsPermissions } from '~/lib/constants/permissions.constants';
import useHasAuth0Permission from '~/lib/hooks/useHasAuth0Permission';
import { isPathMatch } from '~/lib/util/nav.util';
import util from '~/lib/util/util';
import { Path } from '~/routes/constants';

const OPS_NAV_LINKS = [
  {
    iconName: FAIconName.arrowsRotateRegular,
    id: 'scriptsure-sync-dashboard',
    label: 'ScriptSure sync',
    path: Path.OPS_SCRIPTSURE_SYNC_DASHBOARD,
    permission: OpsPermissions.OPS_SCRIPTSURE_SYNC_DASHBOARD,
  },
];

const OpsNav = () => {
  const history = useHistory();
  const { pathname } = history.location;
  const hasScriptSureSyncDashboard = useHasAuth0Permission(
    OpsPermissions.OPS_SCRIPTSURE_SYNC_DASHBOARD,
  );

  const navLinks = useMemo(() => {
    const grantedPermissions: Record<OpsPermissions, boolean> = {
      [OpsPermissions.OPS_SCRIPTSURE_SYNC_DASHBOARD]:
        hasScriptSureSyncDashboard,
    };
    return OPS_NAV_LINKS.filter(
      ({ permission }) => grantedPermissions[permission],
    );
  }, [hasScriptSureSyncDashboard]);

  const selectedNavItemId = useMemo(
    () => navLinks.find(({ path }) => isPathMatch(path, pathname))?.id,
    [navLinks, pathname],
  );

  return (
    <ChakraFlex
      as="nav"
      backgroundColor="primary.10"
      direction="column"
      height="-webkit-fill-available"
      padding={`${NAV_TOP_PADDING}px ${NAV_HORIZONTAL_PADDING}px ${NAV_BOTTOM_PADDING}px`}
      width={SIDE_NAV_WIDTH}
      zIndex={100}
    >
      <ChakraFlex
        justifyContent="left"
        onClick={() => history.push(Path.OPS)}
        paddingLeft="6px"
        style={{ cursor: 'pointer' }}
      >
        <OpsLogo />
      </ChakraFlex>
      <ChakraFlex
        direction="column"
        height="-webkit-fill-available"
        justifyContent="space-between"
        paddingTop="32px"
      >
        <ChakraFlex
          direction="column"
          gap="12px"
          px="8px"
          sx={{ '& > *': { cursor: 'pointer' } }}
        >
          {navLinks.map(({ iconName, id, label, path }) => (
            <NavItem
              iconName={iconName}
              isSelected={selectedNavItemId === id}
              key={`NavItem__${id}`}
              label={label}
              onClick={() => history.push(path)}
              tooltipText={label}
            />
          ))}
        </ChakraFlex>
        <ChakraBox>
          <AccountMenu />
          {VITE_MAUD_APP_VERSION && (
            <ChakraFlex marginTop="12px">
              <Text color="secondary.50" variant="caption">
                {util.getAppVersion()}
              </Text>
            </ChakraFlex>
          )}
        </ChakraBox>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default OpsNav;
